// =============================================================================
// Title suggestions for the new-event form.
//
// As the user types a title, offer earlier events in the same calendar whose
// name matches, so a recurring appointment ("Physiotherapie", "Training") is
// two taps instead of a form. Picking one copies that event wholesale — label,
// times, duration, notes, repeat rule, participants — onto the event being
// created. It never links to or edits the source: the result is a fresh event
// with its own id, re-anchored on the day the user picked.
// =============================================================================

import { addDays, format, parseISO } from 'date-fns';

export const MAX_SUGGESTIONS = 5;

// Earlier events whose title contains `query` (case-insensitive), one per
// distinct title, most recently *dated* first so the times offered are the
// ones used last. Empty query → nothing, so the list only appears once the
// user has started typing.
export const suggestEvents = (events, query, limit = MAX_SUGGESTIONS) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const seen = new Set();
  return [...(events || [])]
    .filter((ev) => (ev.title || '').toLowerCase().includes(q))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .filter((ev) => {
      const key = (ev.title || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
};

const DAY_MS = 86400000;
// Whole days between two yyyy-mm-dd strings (ISO dates parse as UTC midnight,
// so this is exact and DST-proof).
const spanDays = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);

// The repeat rule, carried over but re-anchored on the new start date.
// `exceptions` are the *old* series' deleted days and mean nothing to a new
// one. An `until` that already lies before the new start would make an empty
// series, so it is dropped rather than copied.
const reanchorRecurrence = (rule, newDate) => {
  if (!rule || !rule.freq) return null;
  const { exceptions, ...rest } = rule;
  if (rest.endType === 'until' && rest.until && newDate && rest.until < newDate) {
    const { until, ...noUntil } = rest;
    return { ...noUntil, endType: 'never' };
  }
  return rest;
};

// Everything from `source` lands on the draft except the start date, which
// stays the day the user opened. A multi-day source keeps its *length*: the
// end date is recomputed from the new start.
export const applySuggestionTo = (draft, source) => {
  let endDate = '';
  if (draft.date && source.date && source.endDate && source.endDate > source.date) {
    endDate = format(addDays(parseISO(draft.date), spanDays(source.date, source.endDate)), 'yyyy-MM-dd');
  }
  return {
    ...draft,
    title: source.title,
    categoryId: source.categoryId || draft.categoryId,
    allDay: !!source.allDay,
    startTime: source.allDay ? '' : source.startTime || '',
    endTime: source.allDay ? '' : source.endTime || '',
    endDate,
    notes: source.notes || '',
    recurrence: reanchorRecurrence(source.recurrence, draft.date),
    participantIds: Array.isArray(source.participantIds)
      ? [...source.participantIds]
      : draft.participantIds,
    linkedSportName: source.linkedSportName || null
  };
};
